import { useState } from "react";
import { ResponseWithDataObject } from "@api/internal/utils/types";
import { objectifyShareCountByArticleId } from "@features/pulse/utils/helpers";
import {
  PulseShareCount,
  PulseShareCountData,
} from "@features/pulse/utils/types";
import { tryCatch } from "@typings/TryCatch";
import axios from "axios";

type GetShareCountProps = {
  articleId?: string;
  initialShareCount?: number;
};
/**
 * This hook fetches and manages the share count for a given article.
 * It provides functionality to increment the share count and returns the current share count
 */
export const useGetShareCount = ({
  articleId,
  initialShareCount = 0,
}: GetShareCountProps) => {
  const [shareCount, setShareCount] = useState<number>(initialShareCount);

  const getShareCount = async (articleIds: string[]) => {
    if (!articleIds || articleIds.length === 0) return;

    const payload = {
      article_ids: articleIds.join(","),
    };

    const request = axios.get<PulseShareCountData>(
      "/_plat/api/v1/articles/share",
      { params: payload }
    );

    const { data, error } =
      await tryCatch<ResponseWithDataObject<PulseShareCountData>>(request);

    if (error) return;
    const responseData = data?.data;
    const shareData = objectifyShareCountByArticleId(
      responseData?.articles || []
    );

    return shareData;
  };

  const incrementShareCount = async () => {
    if (!articleId) return;

    const request = axios.post<PulseShareCount>("/_plat/api/v1/article/share", {
      article_id: articleId,
    });

    const { data, error } =
      await tryCatch<ResponseWithDataObject<PulseShareCount>>(request);

    if (error) return;

    const responseData = data?.data;
    const shareCount = responseData?.share_count || 0;

    setShareCount(shareCount);
  };

  return { shareCount, incrementShareCount, getShareCount };
};
